import React, { useState } from 'react';

const ProfileCard = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedSolution, setSelectedSolution] = useState(null);
  const [showContactInPopup, setShowContactInPopup] = useState(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Vul dit veld in';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Vul dit veld in';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ongeldig e-mailadres';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Vul dit veld in';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Vul dit veld in';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Clear errors and submit to backend
    setErrors({});
    
    // Show success popup IMMEDIATELY
    setShowSuccessPopup(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
    setSelectedSolution(null);
    setShowContactInPopup(false);
    
    // Send email in background (fire and forget)
    fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    }).catch(() => {
      // Silent fail - user already sees success
    });
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleContactClick = (solutionName) => {
    setShowContactInPopup(true);
  };

  return (
    <>
      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowSuccessPopup(false)}></div>
          <div className="card-dark p-6 max-w-sm w-full relative z-10">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-[#ffffff] rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-[#0a0a0a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-bold text-white text-center mb-2">Bedankt voor je bericht!</h3>
            <p className="text-gray-400 text-center text-sm">
              Ik neem zo snel mogelijk contact met je op.
            </p>
            <button
              onClick={() => setShowSuccessPopup(false)}
              className="mt-4 w-full py-2 bg-[#2a2a2a] text-white rounded-lg hover:bg-[#3a3a3a] transition-colors text-sm"
            >
              Sluiten
            </button>
          </div>
        </div>
      )}

      {/* Solution Details Popup */}
      {selectedSolution && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
          <div className="absolute inset-0 bg-black bg-opacity-70" onClick={() => {
            setSelectedSolution(null);
            setShowContactInPopup(false);
          }}></div>
          <div className="card-dark p-6 md:p-8 max-w-2xl w-full relative z-10 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#ffffff]/10 rounded-lg text-[#ffffff]">
                  {selectedSolution.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">{selectedSolution.name}</h3>
                  <p className="text-gray-400 text-sm mt-1">{selectedSolution.description}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedSolution(null);
                  setShowContactInPopup(false);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="space-y-6">
              {!showContactInPopup ? (
                <>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-3">Over deze oplossing</h4>
                    <p className="text-gray-300 leading-relaxed">{selectedSolution.detailedDescription}</p>
                  </div>

                  <div>
                    <h4 className="text-white font-semibold text-lg mb-3">Voordelen</h4>
                    <ul className="space-y-2">
                      {selectedSolution.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-3 text-gray-300">
                          <svg className="w-5 h-5 text-[#ffffff] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <div className="pt-4 border-t border-[#2a2a2a]">
                    <button
                      onClick={() => handleContactClick(selectedSolution.name)}
                      className="w-full py-2.5 md:py-3 bg-[#ffffff] text-[#0a0a0a] rounded-lg font-semibold hover:bg-[#e5e5e5] transition-colors text-sm md:text-base"
                    >
                      Neem contact op
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Contact Form in Popup */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <button
                        onClick={() => setShowContactInPopup(false)}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <h4 className="text-white font-semibold text-lg">Neem contact op</h4>
                    </div>
                    <p className="text-gray-400 text-sm mb-6">
                      Vul het formulier in. Ik neem contact op.
                    </p>

                    <form onSubmit={handleFormSubmit} className="space-y-4" noValidate>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Naam"
                            className={`px-4 py-3 bg-[#0a0a0a] md:bg-[#1a1a1a] border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-colors w-full ${
                              errors.name ? 'border-red-500' : 'border-[#2a2a2a] focus:border-gray-500'
                            }`}
                          />
                          {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="E-mailadres"
                            className={`px-4 py-3 bg-[#0a0a0a] md:bg-[#1a1a1a] border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-colors w-full ${
                              errors.email ? 'border-red-500' : 'border-[#2a2a2a] focus:border-gray-500'
                            }`}
                          />
                          {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                        </div>
                      </div>
                      <div>
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          placeholder="Onderwerp"
                          className={`w-full px-4 py-3 bg-[#0a0a0a] md:bg-[#1a1a1a] border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-colors ${
                            errors.subject ? 'border-red-500' : 'border-[#2a2a2a] focus:border-gray-500'
                          }`}
                        />
                        {errors.subject && <p className="text-red-400 text-xs mt-1">{errors.subject}</p>}
                      </div>
                      <div>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Schrijf een bericht"
                          rows="4"
                          className={`w-full px-4 py-3 bg-[#0a0a0a] md:bg-[#1a1a1a] border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-colors resize-none ${
                            errors.message ? 'border-red-500' : 'border-[#2a2a2a] focus:border-gray-500'
                          }`}
                        ></textarea>
                        {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
                      </div>
                      <button
                        type="submit"
                        className="w-full py-2.5 md:py-3 bg-[#ffffff] text-[#0a0a0a] rounded-lg font-bold hover:bg-[#e5e5e5] transition-colors text-sm md:text-base"
                      >
                        Verstuur bericht
                      </button>
                    </form>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[560px] mx-auto">
      {/* Content */}
      <div>
        {/* Profile Section */}
        <div className="py-6 md:py-8 text-center">
          <div className="flex justify-center mb-6">
            <img
              src="/Images/Best 3.0.jpg"
              alt="Mehmet Bicilir"
              className="w-28 h-28 md:w-32 md:h-32 rounded-full object-cover"
              loading="eager"
              decoding="sync"
              fetchpriority="high"
              style={{ imageRendering: '-webkit-optimize-contrast' }}
            />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Mehmet Bicilir</h1>
          <p className="text-gray-400 text-lg md:text-xl mb-8">Shopify Developer</p>
          
          {/* Social Links */}
          <div className="flex justify-center gap-3">
            <a
              href="https://www.linkedin.com/in/mehmet-bicilir-9b414717a/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-5 py-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white text-base font-medium hover:bg-[#2a2a2a] hover:border-[#3a3a3a] transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              Mehmet Bicilir
            </a>
          </div>
        </div>

        {/* Projecten en DIEK */}
        <div className="pb-8 md:pb-10 space-y-3">
          {/* Projectenkaart: getekend webshop-mockup */}
          <a
            href="https://diek.nl/projecten"
            target="_blank"
            rel="noopener noreferrer"
            className="group block overflow-hidden rounded-lg border border-white/10 bg-white/[0.04] transition-colors hover:border-white/20"
          >
            <span className="block overflow-hidden border-b border-white/10">
              <svg viewBox="0 0 560 245" className="block w-full h-auto" aria-hidden="true" fill="none">
                <defs>
                  <pattern id="werk-grid" width="28" height="28" patternUnits="userSpaceOnUse">
                    <path d="M28 0H0V28" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                  </pattern>
                  <radialGradient id="werk-glow" cx="280" cy="0" r="320" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#ffffff" stopOpacity="0.12" />
                    <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
                  </radialGradient>
                  <linearGradient id="werk-hero" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#ffffff" stopOpacity="0.22" />
                    <stop offset="1" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                </defs>
                <rect width="560" height="245" fill="url(#werk-grid)" />
                <rect width="560" height="245" fill="url(#werk-glow)" />
                <g className="transition-transform duration-500 group-hover:-translate-y-1">
                  {/* Browservenster */}
                  <path d="M60 52a12 12 0 0 1 12-12h416a12 12 0 0 1 12 12v193H60z" fill="#111111" stroke="rgba(255,255,255,0.14)" />
                  <path d="M60 62h440" stroke="rgba(255,255,255,0.10)" />
                  <circle cx="78" cy="51" r="3" fill="rgba(255,255,255,0.25)" />
                  <circle cx="90" cy="51" r="3" fill="rgba(255,255,255,0.25)" />
                  <circle cx="102" cy="51" r="3" fill="rgba(255,255,255,0.25)" />
                  <rect x="200" y="45" width="160" height="12" rx="6" fill="rgba(255,255,255,0.08)" />
                  {/* Navigatie */}
                  <rect x="84" y="74" width="34" height="10" rx="2" fill="rgba(255,255,255,0.85)" />
                  <rect x="140" y="77" width="22" height="4" rx="2" fill="rgba(255,255,255,0.35)" />
                  <rect x="170" y="77" width="22" height="4" rx="2" fill="rgba(255,255,255,0.35)" />
                  <rect x="200" y="77" width="22" height="4" rx="2" fill="rgba(255,255,255,0.35)" />
                  <circle cx="462" cy="79" r="4" fill="rgba(255,255,255,0.35)" />
                  <circle cx="478" cy="79" r="4" fill="rgba(255,255,255,0.35)" />
                  {/* Hero */}
                  <rect x="84" y="104" width="150" height="9" rx="2" fill="rgba(255,255,255,0.8)" />
                  <rect x="84" y="119" width="118" height="9" rx="2" fill="rgba(255,255,255,0.8)" />
                  <rect x="84" y="137" width="110" height="4" rx="2" fill="rgba(255,255,255,0.35)" />
                  <rect x="84" y="146" width="84" height="4" rx="2" fill="rgba(255,255,255,0.35)" />
                  <rect x="84" y="160" width="58" height="15" rx="7.5" fill="#ffffff" />
                  <rect x="290" y="100" width="192" height="78" rx="6" fill="url(#werk-hero)" stroke="rgba(255,255,255,0.10)" />
                  {/* Productrij */}
                  <g fill="rgba(255,255,255,0.10)">
                    <rect x="84" y="196" width="92" height="52" rx="4" />
                    <rect x="186" y="196" width="92" height="52" rx="4" />
                    <rect x="288" y="196" width="92" height="52" rx="4" />
                    <rect x="390" y="196" width="92" height="52" rx="4" />
                  </g>
                </g>
              </svg>
            </span>
            <span className="flex items-center justify-between gap-4 p-4">
              <span className="min-w-0">
                <span className="block text-xl font-semibold leading-tight tracking-[-0.01em] text-white">Projecten</span>
                <span className="mt-0.5 block text-sm leading-snug text-white/[0.55]">Een selectie van Shopify (Plus) webshops, gebouwd en geoptimaliseerd.</span>
              </span>
              <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 shrink-0 text-white/[0.55]">
                <path d="M4 12 12 4M6 4h6v6" />
              </svg>
            </span>
          </a>

          {/* DIEK */}
          <a
            href="https://diek.nl"
            target="_blank"
            rel="noopener noreferrer"
            className="group grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-center gap-x-4 gap-y-4 sm:gap-x-5 rounded-lg border border-white/10 bg-white/[0.04] p-4 transition-colors hover:border-white/20 hover:bg-white/[0.06]"
          >
            <span aria-hidden="true" className="flex w-16 h-16 shrink-0 items-center justify-center overflow-hidden rounded bg-white text-black">
              <span className="text-[1.375rem] font-bold leading-none tracking-[-0.04em]">DIEK</span>
            </span>
            <span className="min-w-0 flex items-start gap-3">
              <span className="min-w-0 flex-1">
                <span className="block text-xl font-semibold leading-tight tracking-[-0.01em] text-white">DIEK</span>
                <span className="mt-0.5 block text-sm leading-snug text-white/[0.55]">Full-stack Shopify (Plus) development voor DTC- en B2B-merken.</span>
              </span>
              {/* Pijltje, alleen op mobiel */}
              <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="sm:hidden w-5 h-5 shrink-0 mt-0.5 text-white/[0.55]">
                <path d="M4 12 12 4M6 4h6v6" />
              </svg>
            </span>
            {/* Knop, alleen op desktop */}
            <span className="hidden sm:flex h-10 items-center justify-center rounded bg-white px-4 text-sm text-black transition-opacity group-hover:opacity-[0.85]">
              Bekijk
            </span>
          </a>
        </div>

        {/* Contact Form Section */}
        <div className="pb-8 md:pb-10">
          <h2 className="text-lg font-semibold text-white mb-3">Neem contact op</h2>
          
          <p className="text-gray-400 text-sm mb-6">
            Heb je een vraag, wil je samenwerken of ben je benieuwd wat ik voor jou kan betekenen? Stuur me een bericht en ik neem zo snel mogelijk contact met je op.
          </p>

          <form onSubmit={handleFormSubmit} className="space-y-4" noValidate>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Naam"
                  className={`px-4 py-3 bg-[#0a0a0a] md:bg-[#1a1a1a] border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-colors w-full ${
                    errors.name ? 'border-red-500' : 'border-[#2a2a2a] focus:border-gray-500'
                  }`}
                />
                {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="E-mailadres"
                  className={`px-4 py-3 bg-[#0a0a0a] md:bg-[#1a1a1a] border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-colors w-full ${
                    errors.email ? 'border-red-500' : 'border-[#2a2a2a] focus:border-gray-500'
                  }`}
                />
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>
            </div>
            <div>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="Onderwerp"
                className={`w-full px-4 py-3 bg-[#0a0a0a] md:bg-[#1a1a1a] border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-colors ${
                  errors.subject ? 'border-red-500' : 'border-[#2a2a2a] focus:border-gray-500'
                }`}
              />
              {errors.subject && <p className="text-red-400 text-xs mt-1">{errors.subject}</p>}
            </div>
            <div>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Schrijf een bericht"
                rows="4"
                className={`w-full px-4 py-3 bg-[#0a0a0a] md:bg-[#1a1a1a] border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-colors resize-none ${
                  errors.message ? 'border-red-500' : 'border-[#2a2a2a] focus:border-gray-500'
                }`}
              ></textarea>
              {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
            </div>
            <button
              type="submit"
              className="w-full py-2.5 md:py-3 bg-[#ffffff] text-[#0a0a0a] rounded-lg hover:bg-[#e5e5e5] transition-colors font-bold text-sm md:text-base"
            >
              Verstuur bericht
            </button>
          </form>
        </div>

        {/* Divider Line */}
        <div className="border-t border-[#2a2a2a] hidden md:block"></div>

        {/* Footer */}
        <div className="text-center py-6 px-4">
          <p className="text-gray-500 text-sm">© {new Date().getFullYear()} Mehmet Bicilir</p>
        </div>
      </div>
    </div>
    </>
  );
};

export default ProfileCard;
